// FILE: src/components/case-studies/learner/CasePanel.js
// The case workspace (left side on desktop): stage-grouped narrative,
// exhibit (artifact) chips, and an inline artifact viewer. Newly unlocked
// stages get a restrained "New case information" marker — no modals.
//
// Exhibit discoverability (user feedback via Bryan, 2026-07-13): chips carry
// an explicit "Exhibits — click to view" label and view/hide affordance, the
// viewer opens INLINE inside the stage that owns the exhibit (not at the
// panel's bottom), scrolls itself into view, and reports open/close via
// onArtifactOpenChange so hosts (the in-exam scenario strip) can expand.

import React, { useEffect, useRef, useState } from 'react';

const T = {
  line: 'var(--line, #e5e8f1)', lineStrong: 'var(--line-strong, #cfd5e6)',
  muted: 'var(--muted, #5a6582)', brand: 'var(--brand, #2c63ff)',
  brandSoft: 'var(--brand-soft, #eaf0ff)', bg: 'var(--bg, #f6f7fb)', panel: 'var(--panel, #ffffff)',
};

export default function CasePanel({ caseMeta, stages, latestStage, onArtifactViewed, onArtifactOpenChange }) {
  const [artifact, setArtifact] = useState(null);
  const viewerRef = useRef(null);
  // Bring the freshly opened exhibit into view — inside the exam strip the
  // panel is a scroll container, so an off-screen viewer looks like nothing
  // happened. (scrollIntoView is absent in jsdom; optional-call.)
  useEffect(() => {
    if (artifact && viewerRef.current && typeof viewerRef.current.scrollIntoView === 'function') {
      viewerRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [artifact]);
  function toggleArtifact(a) {
    const next = artifact && artifact.id === a.id ? null : a;
    setArtifact(next);
    if (next && onArtifactViewed) onArtifactViewed(a);
    if (onArtifactOpenChange) onArtifactOpenChange(next != null);
  }
  return (
    <div data-testid="case-panel">
      <h2 style={{ fontSize: '1rem', fontWeight: 800, margin: '0 0 2px' }}>{caseMeta.title}</h2>
      <div style={{ color: T.muted, fontSize: '0.8rem', marginBottom: 12 }}>
        You are: {caseMeta.learner_role || 'the project lead'}
      </div>
      {stages.map((st) => (
        <section key={st.sequence} aria-label={`Stage ${st.sequence}: ${st.title}`} style={{ marginBottom: 16 }}>
          {st.sequence > 1 && (
            <div data-testid={`new-info-${st.sequence}`} style={{ display: 'inline-block', padding: '2px 10px',
              borderRadius: 999, fontSize: '0.72rem', fontWeight: 700, background: T.brandSoft, color: T.brand,
              marginBottom: 8, border: st.sequence === latestStage ? `1px solid ${T.brand}` : 'none' }}>
              ✦ New case information — {st.title}
            </div>
          )}
          {st.intro && <p style={{ fontStyle: 'italic', color: T.muted, fontSize: '0.86rem', margin: '0 0 8px' }}>{st.intro}</p>}
          {(st.sections || []).map((sec) => (
            <div key={sec.id} style={{ marginBottom: 10 }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.4px', color: T.muted }}>{sec.title}</div>
              <div style={{ fontSize: '0.88rem', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{sec.content}</div>
            </div>
          ))}
          {(st.artifacts || []).length > 0 && (
            <div style={{ marginTop: 8 }}>
              <div data-testid={`exhibits-label-${st.sequence}`}
                style={{ fontSize: '0.7rem', fontWeight: 800, textTransform: 'uppercase',
                  letterSpacing: '0.4px', color: T.muted, marginBottom: 5 }}>
                📎 Exhibits — click to view
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {st.artifacts.map((a) => {
                  const isOpen = !!(artifact && artifact.id === a.id);
                  return (
                    <button key={a.id} type="button" data-testid={`artifact-chip-${a.id}`}
                      aria-expanded={isOpen}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 7,
                        padding: '8px 14px', borderRadius: 10,
                        border: `1.5px solid ${T.brand}`,
                        background: isOpen ? T.brandSoft : T.panel, color: T.brand,
                        cursor: 'pointer', fontSize: '0.82rem', fontWeight: 700,
                        boxShadow: isOpen ? 'none' : '0 1px 3px rgba(26,33,56,0.12)' }}
                      onClick={() => toggleArtifact(a)}>
                      <span aria-hidden="true">📎</span>
                      <span>{a.title}</span>
                      <span style={{ fontSize: '0.7rem', fontWeight: 700, opacity: 0.75 }}>
                        {isOpen ? '▲ hide' : '▼ view'}
                      </span>
                    </button>
                  );
                })}
              </div>
              {artifact && st.artifacts.some((a) => a.id === artifact.id) && (
                <div ref={viewerRef}>
                  <ArtifactView artifact={artifact} onClose={() => toggleArtifact(artifact)} />
                </div>
              )}
            </div>
          )}
        </section>
      ))}
    </div>
  );
}

export function ArtifactView({ artifact, onClose }) {
  const cj = artifact.content_json;
  return (
    <div data-testid="artifact-view" style={{ border: `1px solid ${T.lineStrong}`, borderRadius: 10, padding: 12, background: T.bg, marginTop: 6 }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
        <strong style={{ fontSize: '0.86rem' }}>📎 {artifact.title}</strong>
        <span style={{ marginLeft: 8, fontSize: '0.7rem', color: T.muted, textTransform: 'uppercase' }}>{artifact.artifact_type}</span>
        <span style={{ flex: 1 }} />
        <button type="button" onClick={onClose} data-testid="artifact-close"
          style={{ border: `1px solid ${T.line}`, background: 'transparent', borderRadius: 6, cursor: 'pointer', fontSize: '0.74rem', padding: '2px 8px' }}>Close</button>
      </div>
      {artifact.description && <div style={{ color: T.muted, fontSize: '0.8rem', marginBottom: 8 }}>{artifact.description}</div>}
      {artifact.asset_url && <img src={artifact.asset_url} alt={artifact.alt_text || ''} style={{ maxWidth: '100%', borderRadius: 8 }} />}
      {cj && Array.isArray(cj.columns) && Array.isArray(cj.rows) && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem', background: T.panel }}>
            <thead><tr>{cj.columns.map((c) => (
              <th key={c.key} scope="col" style={{ textAlign: 'left', padding: '6px 8px', borderBottom: `2px solid ${T.lineStrong}` }}>{c.label}</th>
            ))}</tr></thead>
            <tbody>{cj.rows.map((row, i) => (
              <tr key={i}>{cj.columns.map((c) => (
                <td key={c.key} style={{ padding: '5px 8px', borderBottom: `1px solid ${T.line}` }}>{String(row[c.key] ?? '')}</td>
              ))}</tr>
            ))}</tbody>
          </table>
        </div>
      )}
      {cj && cj.subject !== undefined && (
        <div style={{ background: T.panel, borderRadius: 8, padding: 10, fontSize: '0.84rem' }}>
          <div><strong>From:</strong> {cj.from}</div>
          <div><strong>To:</strong> {cj.to}</div>
          <div><strong>Subject:</strong> {cj.subject}</div>
          <hr style={{ border: 'none', borderTop: `1px solid ${T.line}` }} />
          <div style={{ whiteSpace: 'pre-wrap' }}>{cj.body}</div>
        </div>
      )}
      {cj && cj.body !== undefined && cj.subject === undefined && (
        <div style={{ whiteSpace: 'pre-wrap', fontSize: '0.86rem' }}>{cj.body}</div>
      )}
    </div>
  );
}
